import { useState, useEffect, useRef } from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { useAtom } from "jotai";
import { AccordionItem } from "../AccordianItem";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import { Group, Radio, useMantineColorScheme } from "@mantine/core";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useTranslation } from "react-i18next";
import MGAAPI from "../../api/index";
import displayError from "../../constants/utils";
import { saveDisabledAtom } from "../../utils/atoms/saveDisabledAtom";
import EVCSAttributesForm from "./EVCSAttributesForm";
import fileDownload from "js-file-download";
import { getUtcMidnightIso } from "../../utils/helpers";

const MGA_API = new MGAAPI();

const EVCSUploadMethod = ({
  form,
  evcsParameters,
  loadStartDate,
  updateEvcsState,
  openSteps,
  projectDetails,
  setLoadingComp,
  getFieldAttributes
}) => {
  const { t } = useTranslation();
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const { colorScheme } = useMantineColorScheme();
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const [, setSaveDisabled] = useAtom(saveDisabledAtom);
  const containerRef = useRef(null);
  const [active, setActive] = useState(null);

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  const [dataAssetEvcs, setDataAssetEvcs] = useState(
    evcsParameters?.profileFile || []
  );
  const evcsFileRef = useRef(null);
  const [displayLoader, setDisplayLoader] = useState(false);

  // Sync chart data when atom profileFile changes (e.g. on version switch)
  useEffect(() => {
    setDataAssetEvcs(evcsParameters?.profileFile || []);
  }, [evcsParameters?.profileFile]);

  const profileDetail = evcsParameters?.profileDetail || {};

  const handleFileInputChange = (file) => {
    if (!file) return;
    evcsFileRef.current = file;
    setDataAssetEvcs([]);
    updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
  };

  const handleGetProfileClick = async () => {
    const fileToUpload = evcsFileRef.current;
    if (!fileToUpload) return;

    setDisplayLoader(true);
    const recurrence = profileDetail.recurrence || "YEARLY";
    const isDaily = recurrence === "DAILY";
    const payload = {
      startDate: isDaily
        ? getUtcMidnightIso(loadStartDate)
        : getUtcMidnightIso(profileDetail.startDate),
      startTime: isDaily ? "00:00" : (profileDetail.startTime || "00:00"),
      componentType: "EVCS",
      recurrence,
      units: profileDetail.units,
      timeResolution: profileDetail.timeResolution || "15",
      datasource: "Upload",
    };
    const formData = new FormData();
    formData.append("file", fileToUpload);
    formData.append(
      "profileDetail",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );

    try {
      setSaveDisabled(true);
      const res = await MGA_API.getProfile(formData);
      setDataAssetEvcs(res);
      updateEvcsState(
        "componentAttributes.evcsParameters",
        "profileFile",
        res
      );
      setSaveDisabled(false);
      setDisplayLoader(false);
      displayError("success", "File uploaded successfully!");
    } catch (error) {
      setDataAssetEvcs([]);
      updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
      form.setFieldValue(
        "componentAttributes.evcsParameters.profileDetail.evcsFile",
        null
      );
      evcsFileRef.current = null;
      setSaveDisabled(false);
      setDisplayLoader(false);
      displayError(
        "error",
        error?.response?.data?.message || "Error occurred while uploading file"
      );
    }
  };

  const handleFileDownload = () => {
    setLoadingComp(true);
    MGA_API.downloadImportTemplate("EVCS")
      .then(({ data, fileName }) => {
        fileDownload(data, fileName || "EVCSFile");
        displayError("success", "File Downloaded successfully!");
      })
      .catch((error) => {
        console.log("Error Downloading File:", error);
      })
      .finally(() => {
        setLoadingComp(false);
      });
  };

  const handleToggle = (index) => {
    setActive((prev) => (prev === index ? null : index));
  };

  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-evcs-2"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };

  const header = () => (
    <span data-id="accordion-evcs-2">
      <span className="stepText col-md-1 px-0">
        {`Step ${addedStepsSeries.indexOf("evcs") + 1}`}: B.{" "}
      </span>
      <span
        className="ml-2 mt-0 col-md-8 px-0"
        style={{ color: colorScheme === "dark" ? "#d5d7e0" : "#475f7b" }}
      >
        Upload EVCS Power profile and set associated configuration parameters
      </span>
    </span>
  );

  const children = () => (
    <div
      className="row"
      ref={containerRef}
      data-id="accordion-evcs-2"
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      role="button"
    >
      <se-loading loading={displayLoader} />
      <Radio.Group
        value={profileDetail.recurrence || "YEARLY"}
        onChange={(value) => {
          const currentRecurrence = profileDetail.recurrence || "YEARLY";
          updateEvcsState(
            "componentAttributes.evcsParameters.profileDetail",
            "recurrence",
            value
          );
          if (value !== currentRecurrence) {
            evcsFileRef.current = null;
            setDataAssetEvcs([]);
            updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
            updateEvcsState(
              "componentAttributes.evcsParameters.profileDetail",
              "evcsFileClicked",
              false
            );
            form.setFieldValue(
              "componentAttributes.evcsParameters.profileDetail.evcsFile",
              null
            );
          }
        }}
        mb="md"
      >
        <Group mt="xs">
          <Radio value="YEARLY" label="Yearly" color="seGreen.0" data-id="evcs-radio-yearly" {...getFieldAttributes("field-4")} data-id="field-4"/>
          <Radio value="DAILY" label="Daily" color="seGreen.0" data-id="evcs-radio-daily" {...getFieldAttributes("field-3")} data-id="field-3"/>
        </Group>
      </Radio.Group>
      <EVCSAttributesForm
        form={form}
        evcsParameters={evcsParameters}
        dataAssetEvcs={dataAssetEvcs}
        startDate={profileDetail.startDate}
        loadStartDate={loadStartDate}
        updateEvcsState={updateEvcsState}
        handleFileInputChange={handleFileInputChange}
        handleFileClick={handleGetProfileClick}
        handleFileDownload={handleFileDownload}
      />
    </div>
  );

  return (
    <AccordionItem
      id={2}
      handleToggle={handleToggle}
      header={header()}
      key={2}
      active={active}
      children={children()}
      openSteps={openSteps}
      height="fit-content"
    />
  );
};

export default EVCSUploadMethod;
